import React from "react";

import {
  RESULT_MODE_CONFIG,
  getResultModeConfigForVariant,
} from "../../utils/results/resultModeConfig";
import {
  getResultModeIcon,
  getResultModeLabel,
} from "../../utils/results/resultModePresentation";
import { buildResultPath } from "../../utils/results/resultNavigation";
import {
  formatPassFailLabel,
  getQualityStatusPillClasses,
} from "../../utils/results/resultQuality";
import {
  RECORDING_QUALITY_OVERALL_STATES,
  buildRecordingQualityCheck,
  getRecordingQualitySummaryCopy,
  summarizeRecordingQualityChecks,
} from "../../utils/results/recordingQuality";

const qualityStatusClasses = {
  pass: "border-primary/20 bg-primary/10 text-primary",
  fail: "border-red-200 bg-red-50 text-red-700",
};

function isResultFeatureVisible(resultFeatures, featureKey) {
  if (!resultFeatures || typeof resultFeatures !== "object") {
    return true;
  }

  return resultFeatures?.[featureKey] === true;
}

function QualityStatusChip({
  label,
  status,
  t,
  showStatusLabel = true,
  qualityStatusPillsEnabled = false,
}) {
  if (!status) return null;

  const statusLabel = formatPassFailLabel(status === "fail", t);

  if (qualityStatusPillsEnabled && showStatusLabel) {
    return (
      <span className="inline-flex min-h-5 max-w-full items-center gap-1.5 text-[11px] font-medium leading-tight text-typography">
        <span className="truncate">{label}</span>
        <span
          role="img"
          aria-label={statusLabel}
          title={statusLabel}
          className={[
            "inline-flex h-3 w-8 shrink-0 rounded-full align-middle",
            getQualityStatusPillClasses(status),
          ].join(" ")}
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex min-h-5 max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-tight ${
        qualityStatusClasses[status] || qualityStatusClasses.fail
      }`}
    >
      <span className="truncate">{label}</span>
      {showStatusLabel ? (
        <span>{statusLabel}</span>
      ) : null}
    </span>
  );
}

function QualityMessage({ children }) {
  if (!children) return null;

  return (
    <span className="w-full text-xs leading-tight text-greyscale-3">
      {children}
    </span>
  );
}

function RecordingQualityCardSummary({ summary, t }) {
  const summaryCopy = getRecordingQualitySummaryCopy(t, summary, {
    surface: "history",
  });

  if (!summaryCopy) {
    return null;
  }

  const isRepeat =
    summary.overallState === RECORDING_QUALITY_OVERALL_STATES.REPEAT;
  const hasInlineRepeatBody = isRepeat && summaryCopy.title && summaryCopy.body;

  return (
    <div
      className="flex w-full min-w-0 flex-col"
      aria-label={summaryCopy.accessibleLabel}
    >
      {hasInlineRepeatBody ? (
        <div className="flex w-full min-w-0 flex-wrap items-center gap-1 text-sm leading-relaxed whitespace-normal">
          <span className="font-semibold text-amber-800">
            {summaryCopy.title}
          </span>
          <span className="font-normal text-typography" aria-hidden="true">
            ·
          </span>
          <span className="font-normal text-typography">
            {summaryCopy.body}
          </span>
        </div>
      ) : summaryCopy.title ? (
        <span className="min-w-0 text-sm font-medium leading-relaxed whitespace-normal text-typography">
          {summaryCopy.title}
        </span>
      ) : null}
      {summaryCopy.body && !hasInlineRepeatBody ? (
        <span className="w-full text-sm leading-relaxed whitespace-normal text-typography">
          {summaryCopy.body}
        </span>
      ) : null}
    </div>
  );
}

function buildResultCardQualitySummary(row, t, resultFeatures = null) {
  const checks = [];
  const recordingTimestamp = row?.timestamp || null;
  const respiratoryQuality = row.rrQuality || row.signalQuality;
  const showSignalQuality = isResultFeatureVisible(resultFeatures, "signalQuality");
  const showSpirometryQuality = isResultFeatureVisible(
    resultFeatures,
    "spirometryQuality",
  );
  const showCardiacQuality = isResultFeatureVisible(
    resultFeatures,
    "cardiacQuality",
  );

  if (row.variant === RESULT_MODE_CONFIG.spirometry.variant) {
    if (showSignalQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "signal",
          label: t("result.spiro.signalQuality", "Signal Quality"),
          rawStatus: row.signalQuality,
          recordingTimestamp,
        }),
      );
    }

    if (showSpirometryQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "spirometry",
          label: t("result.spiro.spirometryQuality", "Spirometry Quality"),
          rawStatus: row.spirometryQuality,
          recordingTimestamp,
        }),
      );
    }
  } else if (row.variant === RESULT_MODE_CONFIG.postExertion.variant) {
    if (showSignalQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "respiratory",
          label: t(
            "result.postExertion.respiratorySignalQuality",
            "Respiratory Signal Quality",
          ),
          rawStatus: respiratoryQuality,
          recordingTimestamp,
        }),
      );
    }
  } else if (row.variant === RESULT_MODE_CONFIG.pulse.variant) {
    if (showCardiacQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "cardiac",
          label: t("result.cardiopulmonary.cardiacQuality", "Cardiac Quality"),
          rawStatus: row.hrQuality,
          recordingTimestamp,
        }),
      );
    }
  } else if (showCardiacQuality || showSignalQuality) {
    if (showCardiacQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "cardiac",
          label: t("result.cardiopulmonary.cardiacQuality", "Cardiac Quality"),
          rawStatus: row.hrQuality,
          recordingTimestamp,
        }),
      );
    }

    if (showSignalQuality) {
      checks.push(
        buildRecordingQualityCheck({
          key: "respiratory",
          label: t(
            "result.cardiopulmonary.respiratoryQuality",
            "Respiratory Quality",
          ),
          rawStatus: respiratoryQuality,
          recordingTimestamp,
        }),
      );
    }
  }

  return summarizeRecordingQualityChecks(checks);
}

/**
 * Builds the per-mode quality chips + guidance messages shown in the body of a
 * result list card. Returns `{ chips, messages }` (each an array of JSX nodes)
 * so the caller can render the chips as a wrap row with any guidance messages
 * stacked underneath — the messages are always last, never interleaved.
 */
function buildResultQualityDetails(
  row,
  t,
  resultFeatures = null,
  _qualityStatusPillsEnabled = false,
) {
  if (!row) return { chips: [], messages: [] };

  // Collect chips and messages separately so the caller can render the pass/fail
  // chips as a wrap row with any guidance messages stacked underneath — rather
  // than interleaved per quality metric (chip, message, chip, …).
  const chips = [];
  const messages = [];
  const addFailureBadge = (key, label) => {
    if (!label) return;

    chips.push(
      <QualityStatusChip
        key={key}
        label={label}
        status="fail"
        t={t}
        showStatusLabel={false}
      />,
    );
  };
  const addMessage = (key, message) => {
    if (!message) return;

    messages.push(<QualityMessage key={key}>{message}</QualityMessage>);
  };

  if (row.uploadFailed) {
    addFailureBadge(
      "upload-failed",
      t("result.uploadFailedTitle", "Upload failed"),
    );
    return { chips, messages };
  }

  if (row.variant === RESULT_MODE_CONFIG.manualAudio.variant) {
    if (row.manualAudioComplete) {
      addMessage(
        "manual-audio-complete",
        t(
          "result.manualAudio.completeTitle",
          "Manual audio recording complete",
        ),
      );
    }

    return { chips, messages };
  }

  const summary = buildResultCardQualitySummary(row, t, resultFeatures);

  if (summary.overallState !== RECORDING_QUALITY_OVERALL_STATES.UNAVAILABLE) {
    messages.push(
      <RecordingQualityCardSummary
        key="recording-quality"
        summary={summary}
        t={t}
      />,
    );
  }

  return { chips, messages };
}

function buildResultCardMeta(row) {
  const time = String(row?.time || "").trim();
  const date = String(row?.date || "").trim();

  return {
    meta: [time, date].filter(Boolean).join(" "),
    secondaryMeta: "",
  };
}

/**
 * Maps a measurement row into the view model consumed by the results list
 * (label, date/time meta, mode icon, quality chips, and the detail-page link).
 * Returns null for rows without an id or a recognised recording mode.
 */
export function buildResultListItem(
  row,
  t,
  resultFeatures = null,
  qualityStatusPillsEnabled = false,
) {
  const config = getResultModeConfigForVariant(row?.variant);
  const workflowStage =
    typeof row?.rawMeasurement?.workflow?.stage === "string"
      ? row.rawMeasurement.workflow.stage.trim().toLowerCase()
      : null;

  if (!row?.id || !config) {
    return null;
  }

  if (
    row.variant === RESULT_MODE_CONFIG.sixMinuteWalk.variant &&
    workflowStage === "pre"
  ) {
    return null;
  }

  const { meta, secondaryMeta } = buildResultCardMeta(row);

  return {
    id: `${row.variant}:${row.id}`,
    label: getResultModeLabel(row.variant, t),
    meta,
    secondaryMeta,
    details: buildResultQualityDetails(
      row,
      t,
      resultFeatures,
      qualityStatusPillsEnabled,
    ),
    icon: getResultModeIcon(row.variant),
    to: buildResultPath({
      variant: row.variant,
      docid: row.id,
      context: config.context,
      tab: config.tab,
    }),
  };
}
